/**
 * Botón de login biométrico
 * Sprint 7 - Muestra el ícono de huella y gestiona el flujo
 */

import { useCallback } from 'react';
import { useBiometricStore } from '../../stores/biometricStore';
import { getBiometryName } from '../../services/biometric';
import { cn } from '../../utils';

/** SVG de huella digital */
function FingerprintIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
            <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
            <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
            <path d="M2 12a10 10 0 0 1 18-6" />
            <path d="M2 16h.01" />
            <path d="M21.8 16c.2-2 .131-5.354 0-6" />
            <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
            <path d="M8.65 22c.21-.66.45-1.32.57-2" />
            <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
        </svg>
    );
}

interface BiometricLoginButtonProps {
    onAuthenticated: (email: string, token: string) => void;
    showDivider?: boolean;
}

export function BiometricLoginButton({
    onAuthenticated,
    showDivider = true,
}: BiometricLoginButtonProps) {
    const {
        isAvailable,
        isEnabled,
        hasCredentials,
        biometryType,
        isAuthenticating,
        error,
        authenticateAndGetToken,
        clearError,
    } = useBiometricStore();

    // Solo mostrar si biométrico está disponible, habilitado y tiene credenciales
    const canShow = isAvailable && isEnabled && hasCredentials;

    const biometryName = biometryType ? getBiometryName(biometryType) : 'biométrico';

    const handlePress = useCallback(async () => {
        clearError();
        const credentials = await authenticateAndGetToken();
        if (credentials) {
            onAuthenticated(credentials.email, credentials.token);
        }
    }, [authenticateAndGetToken, onAuthenticated, clearError]);

    if (!canShow) {
        return null;
    }

    return (
        <div className="mb-6 flex flex-col items-center gap-3">
            <button
                type="button"
                className={cn(
                    'flex h-[72px] w-[72px] items-center justify-center rounded-full border-2 border-primary bg-surface text-primary transition active:scale-95 hover:bg-primary/10 [&_svg]:h-8 [&_svg]:w-8',
                    isAuthenticating && 'pointer-events-none opacity-60'
                )}
                onClick={handlePress}
                disabled={isAuthenticating}
                aria-label={`Iniciar sesión con ${biometryName}`}
            >
                <FingerprintIcon />
            </button>
            <p className="m-0 text-[0.8125rem] text-text-secondary">
                Iniciar con {biometryName}
            </p>
            {error && (
                <p className="m-0 text-[0.8125rem] text-error">{error}</p>
            )}
            {showDivider && (
                <div className="my-2 flex w-full items-center gap-4">
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                    <span className="text-[0.8125rem] uppercase tracking-wide text-text-muted">o</span>
                    <span className="h-px flex-1 bg-border" aria-hidden="true" />
                </div>
            )}
        </div>
    );
}
